<template>
  <!-- Thin wrapper over PrimeVue Dialog (focus trap, Esc, aria-modal, return-focus all handled by it).
       We add: a size scale, a sane mobile-safe max width, an alertdialog role for confirmations, and a
       backdrop that closes by default. Title goes through the `header` prop so PrimeVue wires
       aria-labelledby; body is the default slot; actions go in #footer. -->
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    modal
    :header="title"
    :dismissable-mask="dismissableMask"
    :draggable="false"
    :class="size_class"
    :pt="alert ? { root: { role: 'alertdialog' } } : undefined"
    v-bind="$attrs"
  >
    <template
      v-if="$slots.header"
      #header
      ><slot name="header"
    /></template>
    <slot />
    <template
      v-if="$slots.footer"
      #footer
    >
      <!-- Action row: secondary on the left, primary on the right (Z-pattern end point). -->
      <div class="flex justify-end gap-2">
        <slot name="footer" />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
// Dialog / modal. Wraps PrimeVue Dialog so accessibility (focus management, Escape, aria-modal,
// scrim) comes for free and we only own the conventions: size, mobile width, alertdialog role.
import { computed } from 'vue';
import Dialog from 'primevue/dialog';

defineOptions({ inheritAttrs: false }); // extra attrs (closable, close-on-escape…) fall through to Dialog

const props = defineProps({
  /** Open state (`v-model:visible`). */
  visible: { type: Boolean },
  /** Header text, wired to `aria-labelledby`. Use the `#header` slot to override it entirely. */
  title: { type: String, default: null },
  /** `sm` · `md` · `lg` · `xl`. Every size is capped to the viewport width on phones. */
  size: { type: String, default: 'md', validator: (v) => ['sm', 'md', 'lg', 'xl'].includes(v) },
  /** Sets `role="alertdialog"`. For confirmations and destructive prompts. */
  alert: { type: Boolean },
  /** Clicking the backdrop closes it. Set it false to guard unsaved input. */
  dismissableMask: { type: Boolean, default: true },
});
defineEmits(['update:visible']);

// Widths sit on a small linear scale; max-w keeps the dialog inside the viewport on phones.
const SIZES = {
  sm: 'w-[26rem] max-w-[calc(100vw-2rem)]',
  md: 'w-[32rem] max-w-[calc(100vw-2rem)]',
  lg: 'w-[44rem] max-w-[calc(100vw-2rem)]',
  xl: 'w-[60rem] max-w-[calc(100vw-2rem)]',
};
const size_class = computed(() => SIZES[props.size]);
</script>
